import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import StatCard from '../../components/ui/StatCard';
import Card from '../../components/ui/Card';
import Avatar from '../../components/ui/Avatar';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconTractor,
  IconClipboardList,
  IconAlertTriangle,
  IconDroplet,
  IconBuilding,
  IconArrowRight,
  IconShieldCheck,
  IconUsers,
  IconTool,
  IconBell,
} from '@tabler/icons-react';

export default function GroupOverview() {
  const { t, language } = useTranslation();
  const { userProfile, org, orgId } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [machines, setMachines] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [defects, setDefects] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const brandColour = org?.brandColour || 'var(--color-brand)';

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [sitesSnap, incidentsSnap, defectsSnap, announcementsSnap] = await Promise.all([
          getDocs(collection(db, 'organisations', orgId, 'sites')),
          getDocs(query(collection(db, 'organisations', orgId, 'incidents'), orderBy('detectedAt', 'desc'), limit(5))),
          getDocs(query(collection(db, 'organisations', orgId, 'defects'), orderBy('reportedAt', 'desc'), limit(5))),
          getDocs(query(collection(db, 'organisations', orgId, 'announcements'), orderBy('sentAt', 'desc'), limit(3))),
        ]);

        const siteList = sitesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Load machines and checks per site
        const enrichedSites = await Promise.all(siteList.map(async (site) => {
          const [machinesSnap, tanksSnap] = await Promise.all([
            getDocs(collection(db, 'organisations', orgId, 'machines')),
            getDocs(collection(db, 'organisations', orgId, 'dervTanks')),
          ]);

          const siteMachines = machinesSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(m => m.siteId === site.id);

          const siteTanks = tanksSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(t => t.siteId === site.id);

          const blocked = siteMachines.filter(m => m.isBlocked).length;

          const lowestTank = siteTanks.reduce((min, t) => {
            if (!t.capacityLitres) return min;
            const pct = Math.round((t.currentLitres / t.capacityLitres) * 100);
            return pct < min ? pct : min;
          }, 100);

          const status = blocked > 0 ? 'danger' : lowestTank <= 15 ? 'danger' : lowestTank <= 30 ? 'warn' : 'ok';

          return {
            ...site,
            machineCount: siteMachines.length,
            blocked,
            tankPct: lowestTank,
            status,
          };
        }));

        // Load all machines
        const allMachinesSnap = await getDocs(collection(db, 'organisations', orgId, 'machines'));
        const allMachines = allMachinesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        setSites(enrichedSites);
        setMachines(allMachines);
        setIncidents(incidentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setDefects(defectsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAnnouncements(announcementsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('dashboard.greeting_morning') : hour < 17 ? t('dashboard.greeting_afternoon') : t('dashboard.greeting_evening');
  const dateStr = new Date().toLocaleDateString(language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const totalMachines = machines.length;
  const totalBlocked = machines.filter(m => m.isBlocked).length;
  const openIncidents = incidents.filter(i => i.status === 'open').length;
  const openDefects = defects.filter(d => d.status === 'open').length;

  const statusColour = (s) => s === 'danger' ? 'var(--color-danger)' : s === 'warn' ? 'var(--color-warning)' : 'var(--color-success)';
  const statusBg = (s) => s === 'danger' ? 'var(--color-danger-light)' : s === 'warn' ? 'var(--color-warning-light)' : 'var(--color-success-light)';
  const statusLabel = (s) => s === 'danger' ? t('machines.needs_attention') : s === 'warn' ? t('dashboard.status_monitor') : t('dashboard.status_all_good');

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return `Today ${date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}`;
    if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}`;
    return date.toLocaleDateString(language, { day: 'numeric', month: 'short' });
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', minHeight: '100vh' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>
              {greeting} {userProfile?.name?.split(' ')[0] || t('dashboard.there')}
            </h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 8 }}>{dateStr}</p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              background: brandColour + '18',
              color: brandColour,
              borderRadius: 'var(--radius-full)',
              padding: '3px 10px',
            }}>
              <IconBuilding size={11} stroke={1.5} />
              {org?.name || t('dashboard.group_overview')} · {t('machines.all_sites')}
            </div>
          </div>
          <button
            onClick={() => navigate('/announcements/new')}
            style={{
              background: 'var(--color-brand)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: '8px 16px',
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <IconBell size={14} stroke={1.5} /> {t('dashboard.send_announcement')}
          </button>
        </div>

        {/* Stats */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
            {[1,2,3,4].map(i => (
              <div key={i} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px', height: 88 }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
            <StatCard
              value={totalMachines}
              label={t('dashboard.total_machines')}
              colour="var(--color-text-primary)"
              icon={<IconTractor size={16} color="var(--color-text-secondary)" stroke={1.5} />}
              onClick={() => navigate('/machines')}
            />
            <StatCard
              value={totalBlocked}
              label={t('dashboard.blocked_machines')}
              colour={totalBlocked > 0 ? 'var(--color-danger)' : 'var(--color-success)'}
              icon={<IconTractor size={16} color={totalBlocked > 0 ? 'var(--color-danger)' : 'var(--color-success)'} stroke={1.5} />}
              onClick={() => navigate('/machines')}
            />
            <StatCard
              value={openIncidents}
              label={t('dashboard.open_incidents')}
              colour={openIncidents > 0 ? 'var(--color-danger)' : 'var(--color-success)'}
              icon={<IconAlertTriangle size={16} color={openIncidents > 0 ? 'var(--color-danger)' : 'var(--color-success)'} stroke={1.5} />}
              onClick={() => navigate('/incidents')}
            />
            <StatCard
              value={openDefects}
              label={t('dashboard.open_defects')}
              colour={openDefects > 0 ? 'var(--color-warning)' : 'var(--color-success)'}
              icon={<IconTool size={16} color={openDefects > 0 ? 'var(--color-warning)' : 'var(--color-success)'} stroke={1.5} />}
              onClick={() => navigate('/defects')}
            />
          </div>
        )}

        {/* Site cards */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>{t('dashboard.sites')}</div>
          {loading ? (
            <LoadingSkeleton rows={2} card />
          ) : sites.length === 0 ? (
            <Card>
              <EmptyState
                icon={<IconBuilding size={32} stroke={1.5} />}
                title={t('dashboard.no_sites')}
                message={t('dashboard.no_sites_msg')}
                action={
                  <button
                    onClick={() => navigate('/settings/sites')}
                    style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
                  >
                    {t('dashboard.add_site')}
                  </button>
                }
              />
            </Card>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
              {sites.map(site => (
                <Card
                  key={site.id}
                  onClick={() => navigate('/site-dashboard')}
                  hover
                  style={{ border: `0.5px solid ${site.status === 'danger' ? 'var(--color-danger)' : 'var(--color-border-light)'}` }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: brandColour + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconBuilding size={16} color={brandColour} stroke={1.5} />
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 400 }}>{site.name}</div>
                    </div>
                    <div style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, background: statusBg(site.status), color: statusColour(site.status) }}>
                      {statusLabel(site.status)}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
                    {[
                      { label: t('machines.title'), value: site.machineCount, colour: 'var(--color-text-primary)', icon: IconTractor },
                      { label: t('machines.blocked_one'), value: site.blocked, colour: site.blocked > 0 ? 'var(--color-danger)' : 'var(--color-text-tertiary)', icon: IconTractor },
                      { label: t('dashboard.derv'), value: site.tankPct === 100 ? '—' : `${site.tankPct}%`, colour: site.tankPct <= 15 ? 'var(--color-danger)' : site.tankPct <= 30 ? 'var(--color-warning)' : 'var(--color-success)', icon: IconDroplet },
                    ].map(s => {
                      const SIcon = s.icon;
                      return (
                        <div key={s.label} style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '10px 8px', textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 400, color: s.colour, marginBottom: 2 }}>{s.value}</div>
                          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{s.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {site.tankPct < 100 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <IconDroplet size={11} stroke={1.5} /> {t('dashboard.derv_level')}
                        </span>
                        <span style={{ fontSize: 11, color: site.tankPct <= 15 ? 'var(--color-danger)' : site.tankPct <= 30 ? 'var(--color-warning)' : 'var(--color-success)' }}>
                          {site.tankPct}%
                        </span>
                      </div>
                      <div style={{ height: 3, background: 'var(--color-bg-tertiary)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${site.tankPct}%`, background: site.tankPct <= 15 ? 'var(--color-danger)' : site.tankPct <= 30 ? 'var(--color-warning)' : 'var(--color-success)', borderRadius: 2 }} />
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          {/* Recent incidents */}
          <Card padding="0">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>{t('dashboard.recent_incidents')}</div>
              <span onClick={() => navigate('/incidents')} style={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                {t('common.view_all')} <IconArrowRight size={13} stroke={1.5} />
              </span>
            </div>
            {loading ? (
              <LoadingSkeleton rows={3} />
            ) : incidents.length === 0 ? (
              <EmptyState
                icon={<IconAlertTriangle size={24} stroke={1.5} />}
                title={t('incidents.none')}
                message={t('dashboard.no_incidents_msg')}
              />
            ) : incidents.map((inc, i) => (
              <div
                key={inc.id}
                onClick={() => navigate(`/incidents/${inc.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 18px', borderBottom: i < incidents.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: inc.severity === 'high' ? 'var(--color-danger)' : inc.severity === 'medium' ? 'var(--color-warning)' : 'var(--color-text-tertiary)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inc.triggeredByName || t('common.unknown')}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{inc.assetReg || '—'}</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{formatTime(inc.detectedAt)}</div>
              </div>
            ))}
          </Card>

          {/* Recent defects */}
          <Card padding="0">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>{t('dashboard.recent_defects')}</div>
              <span onClick={() => navigate('/defects')} style={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                {t('common.view_all')} <IconArrowRight size={13} stroke={1.5} />
              </span>
            </div>
            {loading ? (
              <LoadingSkeleton rows={3} />
            ) : defects.length === 0 ? (
              <EmptyState
                icon={<IconTool size={24} stroke={1.5} />}
                title={t('dashboard.no_defects')}
                message={t('dashboard.no_defects_msg')}
              />
            ) : defects.map((defect, i) => (
              <div
                key={defect.id}
                onClick={() => navigate(`/defects/${defect.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 18px', borderBottom: i < defects.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: defect.severity === 'critical' ? 'var(--color-danger)' : defect.severity === 'major' ? 'var(--color-warning)' : 'var(--color-info)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{defect.description || t('dashboard.defect_reported')}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{defect.reportedBy || '—'}</div>
                </div>
                <StatusPill status={defect.severity || 'minor'} />
              </div>
            ))}
          </Card>
        </div>

      </div>
    </Layout>
  );
}